package com.karawek.core.auth.presenter

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.karawek.core.auth.model.OtpChannel
import com.karawek.core.auth.screen.OtpVerificationEvent
import com.karawek.core.auth.screen.OtpVerificationResult
import com.karawek.core.auth.screen.OtpVerificationState
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

abstract class BaseOtpVerificationPresenter(
    private val channel: OtpChannel,
    private val destination: String,
    private val navigator: Navigator,
) : Presenter<OtpVerificationState> {

    /**
     * Called when the OTP should be (re)sent. Implementations should initiate the
     * delivery and return a [Result] indicating success or failure.
     */
    protected abstract suspend fun onSendOtp(): Result<Unit>

    /**
     * Called when the user submits an OTP code for verification. Implementations should
     * verify the code and return a [Result] with the [OtpVerificationResult] to pop on success.
     */
    protected abstract suspend fun onVerifyOtp(otpCode: String): Result<OtpVerificationResult>

    @Composable
    override fun present(): OtpVerificationState {
        val scope = rememberCoroutineScope()

        var state by remember {
            mutableStateOf(
                OtpVerificationState(
                    channel = channel,
                    destination = destination,
                ),
            )
        }

        // Rate-limiting controller — pure Kotlin, no Compose dependencies.
        val resendController = remember { OtpResendController() }

        // Compose-observable mirrors of controller state (drive recomposition).
        var resendAttempts by remember { mutableStateOf(0) }
        var resendCountdownSeconds by remember { mutableStateOf(0) }
        var isLockedOut by remember { mutableStateOf(false) }
        val canResend = !isLockedOut && resendCountdownSeconds == 0

        fun sendOtp() {
            if (!resendController.recordAttempt()) {
                if (resendController.isLockedOut) {
                    isLockedOut = true
                    state = state.copy(
                        errorMessage = "Maximum resend attempts reached. Please start a new session.",
                    )
                }
                return
            }

            resendAttempts = resendController.attempts
            resendCountdownSeconds = resendController.countdownSeconds

            scope.launch {
                state = state.copy(isLoading = true, errorMessage = null)
                onSendOtp().fold(
                    onSuccess = {
                        state = state.copy(isLoading = false)
                    },
                    onFailure = { throwable ->
                        state = state.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Unable to send one-time password.",
                        )
                    },
                )
            }
        }

        fun verifyOtp() {
            scope.launch {
                state = state.copy(isLoading = true, errorMessage = null)
                onVerifyOtp(state.otpCode).fold(
                    onSuccess = { result ->
                        navigator.pop(result)
                    },
                    onFailure = { throwable ->
                        state = state.copy(
                            isLoading = false,
                            errorMessage = throwable.message,
                        )
                    },
                )
            }
        }

        fun onEvent(event: OtpVerificationEvent) {
            when (event) {
                OtpVerificationEvent.OnAppear -> {
                    sendOtp()
                    scope.launch {
                        while (true) {
                            delay(1_000L)
                            resendController.tick()
                            resendCountdownSeconds = resendController.countdownSeconds
                        }
                    }
                }

                OtpVerificationEvent.Back -> navigator.pop(
                    OtpVerificationResult(status = OtpVerificationResult.Status.Cancelled),
                )

                OtpVerificationEvent.Resend -> sendOtp()

                OtpVerificationEvent.Verify -> verifyOtp()

                is OtpVerificationEvent.UpdateOtpCode -> {
                    state = state.copy(otpCode = event.value, errorMessage = null)
                }
            }
        }

        return state.copy(
            canResend = canResend,
            isLockedOut = isLockedOut,
            resendAttempts = resendAttempts,
            resendCountdownSeconds = resendCountdownSeconds,
            eventSink = ::onEvent,
        )
    }
}
